/**
 * NotificationPortal Component
 * Renders notifications in a portal to escape z-index and positioning constraints
 */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { NotificationContainer } from '../NotificationContainer';
import { useNotificationContext } from '../../context/NotificationContext';

/**
 * NotificationPortal Component
 * 
 * Renders ONLY transient notifications (toast popups).
 * Persistent notifications appear only in the NotificationPanel.
 */
export const NotificationPortal: React.FC = () => {
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const { transientNotifications, config, dismiss } = useNotificationContext();

  // Create or find portal root
  useEffect(() => {
    let root = document.getElementById('notification-portal-root');
    
    if (!root) {
      root = document.createElement('div');
      root.id = 'notification-portal-root';
      root.setAttribute('aria-live', 'polite');
      root.setAttribute('aria-label', 'Notifications');
      document.body.appendChild(root);
    }

    setPortalRoot(root);

    // Cleanup on unmount
    return () => {
      // Only remove if we created it and it's empty
      if (root && root.childNodes.length === 0) {
        const element = document.getElementById('notification-portal-root');
        if (element && element.childNodes.length === 0) {
          document.body.removeChild(element);
        }
      }
    };
  }, []);

  // Handle animation completion
  const handleAnimationComplete = (id: string, state: string) => {
    // Animation completion is handled in the context
    // This is just a pass-through for the container
  };

  // Don't render until portal root is ready
  if (!portalRoot) {
    return null;
  }

  // Portal render - only show transient notifications (toast popups)
  return ReactDOM.createPortal(
    <NotificationContainer
      notifications={transientNotifications}
      config={config}
      onDismiss={dismiss}
      onAnimationComplete={handleAnimationComplete}
    />,
    portalRoot
  );
};

/**
 * Wrapper component that includes both provider and portal
 */
export const NotificationSystem: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <>
      {children}
      <NotificationPortal />
    </>
  );
};
